import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useTheme,
  Switch,
  FormControlLabel,
} from '@mui/material';
import PropTypes from 'prop-types';
// import ConditionalConfiguration from './ConditionalConfiguration';
import { useThemeContext } from '../../Context/ThemeContext';
import JoinFunc from './JoinFunc';
import LookupFunc from './LookupFunc';
import RelationFunc from './RelationFunc';
import RegistryFunc from './RegistryFunc';

const FunctionDialog = ({
  open,
  onClose,
  functionType,
  configData,
  onSave,
  icon,
  selectedFields,
  isHandleConnected,
  selectedConnections,
  workflowId,
  updatedConfig,
  selectedEntities,
}) => {
  const [localConfig, setLocalConfig] = useState(configData || {});
  const [sourceEntity, setSourceEntity] = useState('');
  const [sourceField, setSourceField] = useState('');
  const [referenceFields, setReferenceFields] = useState([]);
  const [referenceField, setReferenceField] = useState('');
  const [matchonField, setMatchonField] = useState('');
  const [functionTypejoin, setFunctionTypejoin] = useState('');
  const [referenceEntity, setReferenceEntity] = useState('');
  const [relationEntity, setRelationEntity] = useState('');
  const [destRefEntity, setDestRefEntity] = useState('');
  const [regisrtyField, setRegisrtyField] = useState('');
  const [relationField, setRelationField] = useState('');
  const [relationValue, setRelationValue] = useState('');
  const [relationFields, setRelationFields] = useState([]);
  const [sourceFieldRelation, setSourceFieldRelation] = useState('');
  // const [tab, setTab] = useState('join');
  const [selectedFieldName, setSelectedFieldName] = useState([]);
  const [lookupValue, setLookupValue] = useState('');
  const [sourceFieldReference, setSourceFieldReference] = useState('');
  const [referenceConnection, setReferenceConnection] = useState('');

  // turn "abc" -> "{{abc}}"
  const asToken = (id) => (id ? `{{${id}}}` : '');
  // extract ["abc","def"] from "{{abc}},{{def}}" OR "{{abc}}"
  const parseTokenIds = (val) => {
    if (typeof val !== 'string') return [];
    const matches = val.match(/\{\{(.*?)\}\}/g) || [];
    return matches.map((t) => t.slice(2, -2));
  };

  useEffect(() => {
    if (!open) return;
    const cfg = configData || {};

    // Handle extract variants - load config from variant type if not in config
    if (
      functionType === 'extract_day' ||
      functionType === 'extract_month' ||
      functionType === 'extract_year'
    ) {
      if (!cfg.extract) {
        let extractValue = '';
        if (functionType === 'extract_day') extractValue = 'day';
        else if (functionType === 'extract_month') extractValue = 'month';
        else if (functionType === 'extract_year') extractValue = 'year';
        setLocalConfig((prev) => ({ ...prev, extract: extractValue }));
      } else {
        // Config has extract value, use it
        setLocalConfig((prev) => ({ ...prev, extract: cfg.extract }));
      }
    } else if (functionType === 'extract') {
      // Load from config if available
      if (cfg.extract) {
        setLocalConfig((prev) => ({ ...prev, extract: cfg.extract }));
      }
    }

    // Handle pad variants - load config from variant type if not in config
    if (functionType === 'pad_left' || functionType === 'pad_right') {
      if (!cfg.selectpad) {
        const padValue = functionType === 'pad_left' ? 'left' : 'right';
        setLocalConfig((prev) => ({ ...prev, selectpad: padValue }));
      } else {
        // Config has selectpad value, use it
        setLocalConfig((prev) => ({ ...prev, selectpad: cfg.selectpad }));
      }
      // Also load other pad config values
      if (cfg.total_length !== undefined) {
        setLocalConfig((prev) => ({ ...prev, total_length: cfg.total_length }));
      }
      if (cfg.pad_char !== undefined) {
        setLocalConfig((prev) => ({ ...prev, pad_char: cfg.pad_char }));
      }
    } else if (functionType === 'padLeftRight') {
      // Load from config if available
      if (cfg.selectpad) {
        setLocalConfig((prev) => ({ ...prev, selectpad: cfg.selectpad }));
      }
      if (cfg.total_length !== undefined) {
        setLocalConfig((prev) => ({ ...prev, total_length: cfg.total_length }));
      }
      if (cfg.pad_char !== undefined) {
        setLocalConfig((prev) => ({ ...prev, pad_char: cfg.pad_char }));
      }
    }

    if (functionType === 'join') {
      setReferenceEntity(cfg.reference_entity || '');
      setReferenceField(cfg.reference_field || '');
      setFunctionTypejoin(cfg.apply_function || 'none');
      setMatchonField(cfg.filter?.[0]?.field || '');
      setReferenceConnection(cfg.source_reference || '');
      setSelectedFieldName(parseTokenIds(cfg.filter?.[0]?.value));
    }

    if (functionType === 'lookup') {
      setReferenceEntity(cfg.reference_entity || '');
      setReferenceField(cfg.reference_field || '');
      setReferenceConnection(cfg.source_reference || '');
      setLookupValue(cfg.reference_join_field || '');
    }

    if (functionType === 'relation') {
      setSourceFieldRelation(cfg.source_relationship_field || '');
      setRelationEntity(cfg.relationship_entity || '');
      setRelationField(cfg.relationship_field || '');
      setRelationValue(cfg.value || '');
    }

    if (functionType === 'registry') {
      setSourceFieldReference(cfg.source_reference_field || '');
      setRegisrtyField(cfg.value || '');
      setDestRefEntity(cfg.reference_entity || '');
    }

    // Handle data_masking - initialize with defaults if not in config
    if (functionType === 'data_masking') {
      const fullMasking =
        cfg.full_masking !== undefined ? cfg.full_masking : false;
      setLocalConfig((prev) => ({
        ...prev,
        mask_char: cfg.mask_char !== undefined ? cfg.mask_char : '',
        mask_length: fullMasking
          ? 0
          : cfg.mask_length !== undefined
            ? cfg.mask_length
            : 4,
        full_masking: fullMasking,
        mask_position: fullMasking
          ? null
          : cfg.mask_position !== undefined
            ? cfg.mask_position
            : 'end',
      }));
    }
  }, [open, functionType, configData]);

  // JOIN -> localConfig
  useEffect(() => {
    if (functionType !== 'join') return;
    setLocalConfig((prev) => ({
      ...prev,
      reference_entity: referenceEntity || '',
      reference_field: referenceField || '',
      apply_function: functionTypejoin || 'none',
      source_reference: referenceConnection || '',
      source_connection: selectedConnections?.[0]?.connId || '',
      // If you only support ONE selectedFieldName, use the first one:
      filter: matchonField
        ? [
            {
              field: matchonField,
              operator: '==',
              value: asToken(selectedFieldName?.[0]),
            },
          ]
        : [],
    }));
  }, [
    functionType,
    referenceEntity,
    referenceField,
    functionTypejoin,
    matchonField,
    selectedFieldName,
    referenceConnection,
    selectedConnections,
  ]);

  // LOOKUP -> localConfig
  useEffect(() => {
    if (functionType !== 'lookup') return;
    setLocalConfig((prev) => ({
      ...prev,
      reference_entity: referenceEntity || '',
      reference_field: referenceField || '',
      reference_join_field: lookupValue || '',
      apply_function: 'none',
      source_reference: referenceConnection || '',
      source_connection: selectedConnections?.[0]?.connId || '',
    }));
  }, [
    functionType,
    referenceEntity,
    referenceField,
    lookupValue,
    referenceConnection,
    selectedConnections,
  ]);

  // RELATION -> localConfig
  useEffect(() => {
    if (functionType !== 'relation') return;
    setLocalConfig((prev) => ({
      ...prev,
      source_relationship_field: sourceFieldRelation || '',
      relationship_entity: relationEntity || '',
      relationship_field: relationField || '',
      value: relationValue || '',
    }));
  }, [
    functionType,
    sourceFieldRelation,
    relationEntity,
    relationField,
    relationValue,
  ]);

  // REGISTRY -> localConfig
  useEffect(() => {
    if (functionType !== 'registry') return;
    setLocalConfig((prev) => ({
      ...prev,
      source_reference_field: sourceFieldReference || '',
      reference_entity: destRefEntity || '',
      value: regisrtyField || '',
    }));
  }, [functionType, sourceFieldReference, destRefEntity, regisrtyField]);

  useEffect(() => {
    setLocalConfig(configData || {});
  }, [configData]);

  const { darkMode } = useThemeContext();
  //eslint-disable-next-line
  const theme = useTheme();
  const borderBase = 'rgba(79, 172, 254, 0.1)';

  // const handleSave = () => {
  //   let saveFunctionType = functionType;
  //   let configToSave = { ...localConfig };

  //   // if (functionType === 'to_timestamp' || functionType === 'add_date') {
  //   //   configToSave.date_format = '%Y-%m-%d';
  //   // }

  //   if (functionType === 'extract') {
  //     if (localConfig.extract === 'day') saveFunctionType = 'extract_day';
  //     else if (localConfig.extract === 'month')
  //       saveFunctionType = 'extract_month';
  //     else if (localConfig.extract === 'year')
  //       saveFunctionType = 'extract_year';

  //     delete configToSave.extract;
  //   }

  //   if (functionType === 'padLeftRight') {
  //     if (localConfig.selectpad === 'left') saveFunctionType = 'pad_left';
  //     else if (localConfig.selectpad === 'right')
  //       saveFunctionType = 'pad_right';
  //   }

  //   if (functionType === 'decision') {
  //     saveFunctionType = 'if';
  //   }

  //   console.log('handle show function name:', saveFunctionType);

  //   onSave(configToSave, saveFunctionType);
  // };

  //     delete configToSave.extract;
  //   }
  const handleSave = () => {
    let saveFunctionType = functionType;
    let configToSave = { ...localConfig };

    // 🔁 Extract logic - handle both base and variant types
    if (
      functionType === 'extract' ||
      functionType === 'extract_day' ||
      functionType === 'extract_month' ||
      functionType === 'extract_year'
    ) {
      if (functionType === 'extract') {
        // Base type: convert to variant based on config
        if (localConfig.extract === 'day') saveFunctionType = 'extract_day';
        else if (localConfig.extract === 'month')
          saveFunctionType = 'extract_month';
        else if (localConfig.extract === 'year')
          saveFunctionType = 'extract_year';
      } else {
        // Already a variant type: update if extract value changed
        if (localConfig.extract === 'day' && functionType !== 'extract_day') {
          saveFunctionType = 'extract_day';
        } else if (
          localConfig.extract === 'month' &&
          functionType !== 'extract_month'
        ) {
          saveFunctionType = 'extract_month';
        } else if (
          localConfig.extract === 'year' &&
          functionType !== 'extract_year'
        ) {
          saveFunctionType = 'extract_year';
        }
      }
      delete configToSave.extract;
    }

    // 🔁 Pad logic - handle both base and variant types
    if (
      functionType === 'padLeftRight' ||
      functionType === 'pad_left' ||
      functionType === 'pad_right'
    ) {
      if (functionType === 'padLeftRight') {
        // Base type: convert to variant based on config
        saveFunctionType =
          localConfig.selectpad === 'right' ? 'pad_right' : 'pad_left';
      } else {
        // Already a variant type: update if selectpad value changed
        if (localConfig.selectpad === 'right' && functionType !== 'pad_right') {
          saveFunctionType = 'pad_right';
        } else if (
          localConfig.selectpad === 'left' &&
          functionType !== 'pad_left'
        ) {
          saveFunctionType = 'pad_left';
        }
      }
      delete configToSave.selectpad; // remove UI-only selector
    }

    // 🔁 Decision logic - handle both base and variant types
    if (functionType === 'decision' || functionType === 'if') {
      // Always save as 'if' (variant type)
      saveFunctionType = 'if';
    }

    // 🔁 Date-related logic
    if (functionType === 'to_timestamp' || functionType === 'add_date') {
      configToSave.date_format = '%Y-%m-%d';
    }

    // 🔁 Currency format logic - ensure default values are always included
    if (functionType === 'currency_format') {
      configToSave.currency_symbol = configToSave.currency_symbol || '₹';
      configToSave.decimals = configToSave.decimals ?? 2;
    }

    // 🔁 Data masking logic - handle full_masking
    if (functionType === 'data_masking') {
      if (configToSave.full_masking) {
        // When full_masking is true, set mask_length to 0 and mask_position to null
        configToSave.mask_length = 0;
        configToSave.mask_position = null;
        // Keep mask_char as is (can be empty)
        configToSave.mask_char = configToSave.mask_char ?? '';
      } else {
        // When full_masking is false, ensure mask_position has a value
        if (!configToSave.mask_position) {
          configToSave.mask_position = 'end';
        }
        // Keep mask_char as is (can be empty)
        configToSave.mask_char = configToSave.mask_char ?? '';
      }
    }

    console.log('📌 Saving referenceConnection:', referenceConnection);
    console.log('📌 Saving general config:', configToSave);
    onSave(configToSave, saveFunctionType);
  };

  const renderFields = () => {
    switch (functionType) {
      case 'substring':
        return (
          <>
            {/* Common Fields for substring, aggregation, split, trim */}
            <Box display="flex" gap={2}>
              <TextField
                label="From"
                type="text"
                fullWidth
                value={localConfig.startFrom || ''}
                onChange={(e) =>
                  setLocalConfig({ ...localConfig, startFrom: e.target.value })
                }
                margin="normal"
                InputLabelProps={{ shrink: true }}
                // disabled={!isHandleConnected}
              />
              <TextField
                label="Till"
                type="text"
                fullWidth
                value={localConfig.till || ''}
                onChange={(e) =>
                  setLocalConfig({ ...localConfig, till: e.target.value })
                }
                margin="normal"
                InputLabelProps={{ shrink: true }}
                // disabled={!isHandleConnected}
              />
            </Box>
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.75rem',
                color: 'text.secondary',
                mt: 1.5,
                lineHeight: 1.5,
                backgroundColor: '#f5f7fa',
                p: 1.2,
                borderRadius: 2,
                border: '1px solid #e0e7ef',
              }}
            >
              <strong>Example:</strong> &quot;Customer001&quot;.substring(0, 5)
              → &quot;Custo&quot; <br />
              <strong>0</strong> is an index, <strong>5</strong> is length of
              string
            </Typography>
          </>
        );
      case 'aggregation':
        return (
          <>
            <TextField
              label="Add a text(optional)"
              type="text"
              fullWidth
              optional
              value={localConfig.aggText || ''}
              onChange={(e) =>
                setLocalConfig({ ...localConfig, aggText: e.target.value })
              }
              margin="normal"
              InputLabelProps={{ shrink: true }}
            />
          </>
        );
      case 'trim':
        return <></>;
      case 'field_split':
        // case 'split':
        return (
          <>
            <FormControl fullWidth margin="normal">
              <InputLabel shrink>Split By</InputLabel>
              <Select
                value={localConfig.delimiter || ''}
                onChange={(e) =>
                  setLocalConfig({ ...localConfig, delimiter: e.target.value })
                }
                sx={{
                  borderRadius: '8px',
                }}
                displayEmpty
                notched
                label="Split By"
              >
                <MenuItem value="_">_</MenuItem>
                <MenuItem value="|">|</MenuItem>
                <MenuItem value="~">~</MenuItem>
                <MenuItem value=",">,</MenuItem>
                <MenuItem value="/">/</MenuItem>
                <MenuItem value=":">:</MenuItem>
                <MenuItem value=";">;</MenuItem>
                <MenuItem value="-">-</MenuItem>
                <MenuItem value=".">.</MenuItem>
                <MenuItem value=" ">Space</MenuItem>
              </Select>
            </FormControl>
          </>
        );

      case 'remove_special_characters':
        return (
          <>
            <FormControl fullWidth margin="normal">
              <InputLabel shrink>Select Special Characters</InputLabel>
              <Select
                value={localConfig.characters || ''}
                onChange={(e) =>
                  setLocalConfig({ ...localConfig, characters: e.target.value })
                }
                sx={{
                  borderRadius: '8px',
                }}
                displayEmpty
                notched
                label="Select Special Characters"
              >
                {[
                  '!',
                  '@',
                  '#',
                  '$',
                  '%',
                  '^',
                  '&',
                  '*',
                  '.',
                  ',',
                  ':',
                  ';',
                  '-',
                  '_',
                  '+',
                  '=',
                  '[',
                  ']',
                  '{',
                  '}',
                ].map((char) => (
                  <MenuItem key={char} value={char}>
                    {char}
                  </MenuItem>
                ))}
                <MenuItem value="all">Remove All Special Characters</MenuItem>
              </Select>
            </FormControl>
          </>
        );
      case 'uppercase':
      case 'lowercase':
      case 'median':
      case 'mode':
      case 'length':
        return <p>This function has no configurable parameters.</p>;

      case 'currency_format':
        return (
          <Box display="flex" gap={2}>
            <FormControl fullWidth margin="normal">
              <InputLabel shrink>Currency Symbol</InputLabel>
              <Select
                value={localConfig.currency_symbol || '₹'}
                onChange={(e) =>
                  setLocalConfig({
                    ...localConfig,
                    currency_symbol: e.target.value,
                  })
                }
                label="Currency Symbol"
                sx={{ borderRadius: '8px' }}
                displayEmpty
                notched
              >
                <MenuItem value="₹">₹ (INR)</MenuItem>
                <MenuItem value="$">$ (USD)</MenuItem>
                <MenuItem value="€">€ (EUR)</MenuItem>
                <MenuItem value="£">£ (GBP)</MenuItem>
                <MenuItem value="¥">¥ (JPY)</MenuItem>
                <MenuItem value="₩">₩ (KRW)</MenuItem>
                <MenuItem value="₽">₽ (RUB)</MenuItem>
                <MenuItem value="₺">₺ (TRY)</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Decimals"
              type="number"
              fullWidth
              value={localConfig.decimals ?? 2}
              onChange={(e) =>
                setLocalConfig({
                  ...localConfig,
                  decimals: Math.max(0, Math.min(4, Number(e.target.value))),
                })
              }
              inputProps={{ min: 0, max: 4, step: 1 }}
              margin="normal"
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        );

      case 'to_timestamp':
        return (
          <>
            <Box display="flex" gap={2}>
              <TextField
                label="Date Format"
                fullWidth
                value="%Y-%m-%d"
                margin="normal"
                InputProps={{
                  readOnly: true,
                }}
                InputLabelProps={{ shrink: true }}
                helperText='This format is fixed: %Y-%m-%d (e.g. "2023-12-31")'
              />
            </Box>
          </>
        );

      case 'format_date':
        return (
          <>
            <Box display="flex" gap={2}>
              <FormControl fullWidth margin="normal">
                <InputLabel shrink>Current Format</InputLabel>
                <Select
                  value={localConfig.current_format || ''}
                  onChange={(e) =>
                    setLocalConfig({
                      ...localConfig,
                      current_format: e.target.value,
                    })
                  }
                  label="Current Format"
                  sx={{ borderRadius: '8px' }}
                  displayEmpty
                  notched
                >
                  <MenuItem value="">-- Select Format --</MenuItem>
                  {DATE_FORMAT_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth margin="normal">
                <InputLabel shrink>Target Format</InputLabel>
                <Select
                  value={localConfig.target_format || ''}
                  onChange={(e) =>
                    setLocalConfig({
                      ...localConfig,
                      target_format: e.target.value,
                    })
                  }
                  label="Target Format"
                  sx={{ borderRadius: '8px' }}
                  displayEmpty
                  notched
                >
                  <MenuItem value="">-- Select Format --</MenuItem>
                  {DATE_FORMAT_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </>
        );

      case 'add_date':
        return (
          <>
            <TextField
              label="Date Format"
              // type="date"
              fullWidth
              // value={localConfig.date_format || ''}
              value="%Y-%m-%d"
              // onChange={(e) =>
              //   setLocalConfig({ ...localConfig, date_format: e.target.value })
              // }
              margin="normal"
              InputLabelProps={{ shrink: true }}
              // helperText='Example: %Y-%m-%d for "2023-12-31"'
            />
            <TextField
              label="Days"
              type="number"
              fullWidth
              value={localConfig.days || ''}
              onChange={(e) =>
                setLocalConfig({ ...localConfig, days: e.target.value })
              }
              margin="normal"
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: 0, max: 365, step: 1 }}
              helperText="Enter positive to add days"
            />
          </>
        );

      case 'extract':
      case 'extract_day':
      case 'extract_month':
      case 'extract_year':
        return (
          <>
            <FormControl fullWidth margin="normal">
              <InputLabel shrink>Extract Month/Day</InputLabel>
              <Select
                value={localConfig.extract || ''}
                onChange={(e) =>
                  setLocalConfig({ ...localConfig, extract: e.target.value })
                }
                sx={{
                  borderRadius: '8px',
                }}
                displayEmpty
                notched
                label="Extract Month/Day"
              >
                <MenuItem value="day">Day</MenuItem>
                <MenuItem value="month">Month</MenuItem>
                <MenuItem value="year">Year</MenuItem>
              </Select>
            </FormControl>
          </>
        );

      case 'replace':
        return (
          <>
            <TextField
              label="From this Value"
              type="text"
              fullWidth
              value={localConfig.old || ''}
              onChange={(e) =>
                setLocalConfig({ ...localConfig, old: e.target.value })
              }
              margin="normal"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="To New Value"
              type="text"
              fullWidth
              value={localConfig.new || ''}
              onChange={(e) =>
                setLocalConfig({ ...localConfig, new: e.target.value })
              }
              margin="normal"
              InputLabelProps={{ shrink: true }}
            />
          </>
        );
      case 'padLeftRight':
      case 'pad_left':
      case 'pad_right':
        return (
          <>
            <FormControl fullWidth margin="normal">
              <InputLabel shrink>Select</InputLabel>
              <Select
                value={localConfig.selectpad || ''}
                onChange={(e) =>
                  setLocalConfig({ ...localConfig, selectpad: e.target.value })
                }
                sx={{
                  borderRadius: '8px',
                }}
                displayEmpty
                notched
                label="Select"
              >
                <MenuItem value="left">Left</MenuItem>
                <MenuItem value="right">Right</MenuItem>
              </Select>

              <Box display="flex" gap={2}>
                <TextField
                  label="Total Length"
                  type="text"
                  // integer input
                  fullWidth
                  value={localConfig.total_length || ''}
                  onChange={(e) =>
                    setLocalConfig({
                      ...localConfig,
                      total_length: e.target.value,
                    })
                  }
                  margin="normal"
                  InputLabelProps={{ shrink: true }}
                  // disabled={!isHandleConnected}
                />
                <TextField
                  label="Pad Char"
                  type="text"
                  // symbol iput
                  fullWidth
                  value={localConfig.pad_char || ''}
                  onChange={(e) =>
                    setLocalConfig({ ...localConfig, pad_char: e.target.value })
                  }
                  margin="normal"
                  InputLabelProps={{ shrink: true }}
                  // disabled={!isHandleConnected}
                />
              </Box>
            </FormControl>
          </>
        );
      case 'decision':
      case 'if':
        return (
          <>
            <Typography variant="subtitle2" sx={{ fontWeight: 500, mt: 1 }}>
              Operator
            </Typography>
            <FormControl fullWidth margin="dense">
              <Select
                value={localConfig.operator || ''}
                onChange={(e) =>
                  setLocalConfig({ ...localConfig, operator: e.target.value })
                }
                displayEmpty
                sx={{ borderRadius: '8px', fontSize: '0.938rem' }}
              >
                <MenuItem value="">Select Operator</MenuItem>
                <MenuItem value="+">+</MenuItem>
                <MenuItem value="-">-</MenuItem>
                <MenuItem value="=">=</MenuItem>
                <MenuItem value="!=">!=</MenuItem>
                <MenuItem value=">">&gt;</MenuItem>
                <MenuItem value="<">&lt;</MenuItem>
                <MenuItem value="true">true</MenuItem>
                <MenuItem value="false">false</MenuItem>
              </Select>
            </FormControl>

            <Typography variant="subtitle2" sx={{ fontWeight: 500, mt: 2 }}>
              Value
            </Typography>
            <TextField
              placeholder="Input value here"
              fullWidth
              margin="dense"
              value={localConfig.value || ''}
              onChange={(e) =>
                setLocalConfig({ ...localConfig, value: e.target.value })
              }
            />
          </>
        );

      case 'join':
        return (
          <JoinFunc
            selectedEntities={selectedEntities}
            selectedFields={selectedFields}
            referenceEntity={referenceEntity}
            setReferenceEntity={setReferenceEntity}
            referenceField={referenceField}
            setReferenceField={setReferenceField}
            sourceEntity={sourceEntity}
            setSourceEntity={setSourceEntity}
            sourceField={sourceField}
            setSourceField={setSourceField}
            matchonField={matchonField}
            setMatchonField={setMatchonField}
            functionTypejoin={functionTypejoin}
            setFunctionTypejoin={setFunctionTypejoin}
            lookupValue={lookupValue}
            setLookupValue={setLookupValue}
            referenceFields={referenceFields}
            setReferenceFields={setReferenceFields}
            updatedConfig={updatedConfig}
            workflowId={workflowId}
            setSelectedFieldName={setSelectedFieldName}
            selectedFieldName={selectedFieldName}
            configData={configData}
            referenceConnection={referenceConnection}
            setReferenceConnection={setReferenceConnection}
            selectedConnections={selectedConnections}
          />
        );

      case 'lookup':
        return (
          <LookupFunc
            selectedEntities={selectedEntities}
            selectedFields={selectedFields}
            referenceEntity={referenceEntity}
            setReferenceEntity={setReferenceEntity}
            referenceField={referenceField}
            setReferenceField={setReferenceField}
            sourceEntity={sourceEntity}
            setSourceEntity={setSourceEntity}
            sourceField={sourceField}
            setSourceField={setSourceField}
            lookupValue={lookupValue}
            setLookupValue={setLookupValue}
            referenceFields={referenceFields}
            setReferenceFields={setReferenceFields}
            updatedConfig={updatedConfig}
            workflowId={workflowId}
            referenceConnection={referenceConnection}
            setReferenceConnection={setReferenceConnection}
            selectedConnections={selectedConnections}
          />
        );
      case 'relation':
        return (
          <RelationFunc
            selectedFields={selectedFields}
            workflowId={workflowId}
            updatedConfig={updatedConfig}
            sourceField={sourceField}
            setSourceField={setSourceField}
            sourceEntity={sourceEntity}
            setSourceEntity={setSourceEntity}
            selectedEntities={selectedEntities}
            relationEntity={relationEntity}
            setRelationEntity={setRelationEntity}
            relationField={relationField}
            setRelationField={setRelationField}
            relationValue={relationValue}
            setRelationValue={setRelationValue}
            relationFields={relationFields}
            setRelationFields={setRelationFields}
            setSourceFieldRelation={setSourceFieldRelation}
            sourceFieldRelation={sourceFieldRelation}
            setSelectedFieldName={setSelectedFieldName}
            selectedFieldName={selectedFieldName}
          />
        );
      case 'registry':
        return (
          <RegistryFunc
            selectedFields={selectedFields}
            workflowId={workflowId}
            updatedConfig={updatedConfig}
            sourceField={sourceField}
            setSourceField={setSourceField}
            sourceEntity={sourceEntity}
            setSourceEntity={setSourceEntity}
            selectedEntities={selectedEntities}
            destRefEntity={destRefEntity}
            setDestRefEntity={setDestRefEntity}
            regisrtyField={regisrtyField}
            setRegisrtyField={setRegisrtyField}
            relationValue={relationValue}
            setRelationValue={setRelationValue}
            setSourceFieldReference={setSourceFieldReference}
            sourceFieldReference={sourceFieldReference}
            setSelectedFieldName={setSelectedFieldName}
            selectedFieldName={selectedFieldName}
            hasSavedRegistry={Boolean(configData?.source_reference_field)}
          />
        );

      case 'data_masking': {
        const isFullMasking = localConfig.full_masking || false;
        const maskPositionValue = localConfig.mask_position;
        const isCustomPosition =
          typeof maskPositionValue === 'number' ||
          (maskPositionValue !== 'start' &&
            maskPositionValue !== 'end' &&
            maskPositionValue !== undefined &&
            maskPositionValue !== null);
        const showCustomField = isCustomPosition || maskPositionValue === '';

        return (
          <>
            <FormControlLabel
              control={
                <Switch
                  checked={isFullMasking}
                  onChange={(e) => {
                    const newFullMasking = e.target.checked;
                    setLocalConfig({
                      ...localConfig,
                      full_masking: newFullMasking,
                      // When full_masking is true, set mask_length to 0 and mask_position to null
                      mask_length: newFullMasking
                        ? 0
                        : (localConfig.mask_length ?? 4),
                      mask_position: newFullMasking
                        ? null
                        : localConfig.mask_position || 'end',
                    });
                  }}
                />
              }
              label="Full Masking"
              sx={{ mb: 2 }}
            />

            <TextField
              label="Mask Character"
              type="text"
              fullWidth
              value={localConfig.mask_char ?? ''}
              onChange={(e) => {
                const newValue = e.target.value;
                setLocalConfig({
                  ...localConfig,
                  mask_char: newValue,
                });
              }}
              margin="normal"
              InputLabelProps={{ shrink: true }}
              inputProps={{ maxLength: 1 }}
              helperText="Single character to use for masking"
            />

            <TextField
              label="Mask Length"
              type="number"
              fullWidth
              value={isFullMasking ? 0 : (localConfig.mask_length ?? 4)}
              onChange={(e) => {
                const value = e.target.value;
                // Only allow positive integers
                if (
                  value === '' ||
                  (Number.isInteger(Number(value)) && Number(value) >= 0)
                ) {
                  setLocalConfig({
                    ...localConfig,
                    mask_length: value === '' ? '' : Number(value),
                  });
                }
              }}
              margin="normal"
              InputLabelProps={{ shrink: true }}
              disabled={isFullMasking}
              inputProps={{ min: 0, step: 1 }}
              helperText={
                isFullMasking
                  ? 'Mask length is 0 for full masking'
                  : 'Number of characters to mask (integer only)'
              }
            />

            {!isFullMasking && (
              <>
                <FormControl fullWidth margin="normal">
                  <InputLabel shrink>Mask Position</InputLabel>
                  <Select
                    value={
                      maskPositionValue === 'start' ||
                      maskPositionValue === 'end'
                        ? maskPositionValue
                        : showCustomField
                          ? 'custom'
                          : 'end'
                    }
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === 'custom') {
                        // Set to empty string to show custom input
                        setLocalConfig({
                          ...localConfig,
                          mask_position: '',
                        });
                      } else {
                        setLocalConfig({
                          ...localConfig,
                          mask_position: value,
                        });
                      }
                    }}
                    sx={{ borderRadius: '8px' }}
                    displayEmpty
                    notched
                    label="Mask Position"
                  >
                    <MenuItem value="start">Start</MenuItem>
                    <MenuItem value="end">End</MenuItem>
                    <MenuItem value="custom">Custom (Number/Integer)</MenuItem>
                  </Select>
                </FormControl>

                {showCustomField && (
                  <TextField
                    label="Custom Position (Number/Integer)"
                    type="number"
                    fullWidth
                    value={
                      typeof maskPositionValue === 'number'
                        ? maskPositionValue
                        : ''
                    }
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow empty string or positive integers
                      if (value === '') {
                        setLocalConfig({
                          ...localConfig,
                          mask_position: '',
                        });
                      } else {
                        const numValue = Number(value);
                        if (
                          !isNaN(numValue) &&
                          Number.isInteger(numValue) &&
                          numValue >= 0
                        ) {
                          setLocalConfig({
                            ...localConfig,
                            mask_position: numValue,
                          });
                        }
                      }
                    }}
                    margin="normal"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: 0, step: 1 }}
                    helperText="Enter a number or integer for custom position"
                  />
                )}
              </>
            )}
          </>
        );
      }
      default:
        return <p>No configuration available for this function.</p>;
    }
  };

  const DATE_FORMAT_OPTIONS = [
    { value: '%Y-%m-%d', label: 'YYYY-MM-DD (2023-12-31)' },
    { value: '%d/%m/%Y', label: 'DD/MM/YYYY (31/12/2023)' },
    { value: '%m/%d/%Y', label: 'MM/DD/YYYY (12/31/2023)' },
    { value: '%d-%b-%Y', label: 'DD-Mon-YYYY (31-Dec-2023)' },
    { value: '%d-%B-%Y', label: 'DD-Month-YYYY (31-December-2023)' },
    { value: '%d.%m.%Y', label: 'DD.MM.YYYY (31.12.2023)' },
    { value: '%Y/%m/%d', label: 'YYYY/MM/DD (2023/12/31)' },
    { value: '%m-%d-%Y', label: 'MM-DD-YYYY (12-31-2023)' },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: 3,
          width: 480,
          overflow: 'hidden',
          backgroundColor: darkMode ? '#1e1f22' : '#ffffff',
          border: `1px solid ${borderBase}`,
          boxShadow: darkMode
            ? '0 24px 64px rgba(0,0,0,0.5), inset 0 1px 2px rgba(255,255,255,0.04)'
            : '0 24px 64px rgba(0,0,0,0.15), inset 0 1px 2px rgba(0,0,0,0.06)',
          backdropFilter: 'blur(4px)',
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: darkMode
            ? 'rgba(10, 14, 20, 0.55)'
            : 'rgba(10, 18, 30, 0.35)',
          backdropFilter: 'blur(4px)',

          '& .MuiDialog-container': {
            alignItems: 'center',
          },
        },
      }}
      transitionDuration={{ appear: 250, enter: 250, exit: 200 }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          {icon && (
            <img
              src={icon}
              alt={functionType}
              style={{ width: 24, height: 24 }}
            />
          )}
          <Typography variant="h6">
            {typeof functionType === 'string' && functionType
              ? functionType.charAt(0).toUpperCase() + functionType.slice(1)
              : 'Function'}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {functionType !== 'conditionalConfig' &&
          isHandleConnected &&
          selectedFields?.length > 0 && (
            <Box mb={2}>
              <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                Selected Fields
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                {selectedFields.map((field) => (
                  <Box
                    key={field}
                    sx={{
                      backgroundImage:
                        'linear-gradient(90deg, rgba(59,156,254,0.15), rgba(0,196,239,0.15))',
                      color: '#1976d2',
                      px: 2,
                      py: 0.5,
                      borderRadius: 1,
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      border: '1px solid rgba(59,156,254,0.25)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {field}
                  </Box>
                ))}
              </Box>
            </Box>
          )}

        {renderFields()}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          variant="outlined"
          size="small"
          sx={{
            borderRadius: '8px',
            textTransform: 'none',

            fontSize: '0.825rem',
            fontWeight: 500,
            px: 2,
            borderColor: 'rgba(79, 172, 254, 0.59)',
            color: '#4facfe',
            '&:hover': {
              borderColor: '#4facfe',
              background: 'rgba(79, 172, 254, 0.05)',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!isHandleConnected}
          size="small"
          sx={{
            minWidth: 80,
            height: 30,
            borderRadius: 2,
            px: 2,
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: 'none',
            fontSize: '0.95rem',
            backgroundImage: 'linear-gradient(90deg, #3b9cfe, #00c4ef)',
            color: '#fff',
            '&:hover': {
              backgroundImage: 'linear-gradient(90deg, #3b9cfe, #00c4ef)',
              boxShadow: '0 8px 20px rgba(79, 172, 254, 0.25)',
              transform: 'translateY(-1px)',
            },
            '&:active': {
              transform: 'translateY(0)',
              boxShadow: 'none',
            },
            transition: 'all .18s ease',
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

FunctionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  functionType: PropTypes.string.isRequired,
  configData: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  icon: PropTypes.string,
  selectedFields: PropTypes.arrayOf(PropTypes.string),
  selectedEntities: PropTypes.arrayOf(PropTypes.string),
  selectedConnections: PropTypes.arrayOf(PropTypes.string),
  isHandleConnected: PropTypes.bool,
  workflowId: PropTypes.string,
  updatedConfig: PropTypes.object,
};

export default FunctionDialog;
